<template>
  <form ref="backInStockForm" class="flex w-full flex-col gap-4">
    <div
      class="flex flex-col"
      :class="{ 'pointer-events-none opacity-30': isLoading || showSuccess }"
    >
      <div v-if="!isFinland" class="mb-6">
        <SalutationSelect
          v-model="payload.title as Gender"
          input-id="back_in_stock_subscription_form_salutation"
        />
      </div>

      <div class="flex flex-col justify-between gap-4 md:flex-row">
        <ValidatedInputGroup
          v-slot="{ isValid, inputId }"
          :errors="v.firstName.$errors"
          class="flex-1"
        >
          <TextInput
            v-model="payload.firstName"
            :label="$t('back_in_stock_subscription_form.first_name.label')"
            :input-id="inputId"
            :has-error-message="!isValid"
            type="text"
            required
            autocomplete="first-name"
            @input="v.firstName.$touch"
          />
        </ValidatedInputGroup>
        <ValidatedInputGroup
          v-slot="{ isValid, inputId }"
          :errors="v.lastName.$errors"
          class="flex-1"
        >
          <TextInput
            v-model="payload.lastName"
            :label="$t('back_in_stock_subscription_form.last_name.label')"
            :has-error-message="!isValid"
            :input-id="inputId"
            type="text"
            required
            autocomplete="last-name"
            @input="v.lastName.$touch"
          />
        </ValidatedInputGroup>
      </div>

      <ValidatedInputGroup
        v-slot="{ isValid, inputId }"
        :errors="v.email.$errors"
        error-id="email"
      >
        <TextInput
          v-model="payload.email"
          :label="$t('back_in_stock_subscription_form.email.label')"
          :input-id="inputId"
          :has-error-message="!isValid"
          type="email"
          required
          autocomplete="email"
          @input="v.email.$touch"
        />
      </ValidatedInputGroup>

      <Checkbox
        v-if="doiRequired"
        id="terms"
        :checked="termsAccepted"
        :centered="false"
        @change="termsAccepted = !termsAccepted"
      >
        <template #label>
          <span v-if="!termsAccepted" class="text-red">*</span>
          <i18n-t
            keypath="back_in_stock_subscription_form.newsletter_agreement.terms"
            tag="span"
          >
            <template #link>
              <AgsLink
                :to="toLocalePath('/c/sicherheit-datenschutz/')"
                hover="underline"
                size="sm"
                class="inline-block text-darkGold"
                in-paragraph
                >{{
                  $t(
                    'back_in_stock_subscription_form.newsletter_agreement.privacy_policy',
                  )
                }}
              </AgsLink>
            </template>
          </i18n-t>
        </template>
      </Checkbox>
    </div>

    <Error v-if="isErrorShown" frame>
      {{ $t('back_in_stock_subscription_form.error') }}
    </Error>

    <div>
      <Button
        class="w-full capitalize md:w-auto md:min-w-[50%]"
        :disabled="v.$error || isLoading || (!termsAccepted && doiRequired)"
        @click="submit"
      >
        {{ $t('back_in_stock_subscription_form.cta_button_label') }}
      </Button>
    </div>
  </form>
</template>

<script setup lang="ts">
import useVuelidate from '@vuelidate/core'
import ValidatedInputGroup from '~/components/ui/forms/groups/ValidatedInputGroup.vue'
import TextInput from '~/components/ui/forms/inputs/TextInput.vue'
import Checkbox from '~/components/ui/forms/inputs/Checkbox.vue'
import useCountryCheck from '~/composables/useCountryCheck'
import Error from '~/components/ui/Error.vue'
import useBackInStock from '~/composables/useBackInStock'

const { isInList: isFinland } = useCountryCheck(['fi'])
const { $validation } = useNuxtApp()
const { toLocalePath } = useRouteHelpers()
const {
  doiRequired,
  sendBackInStockRequest,
  showError: isErrorShown,
  showSuccess,
  isLoading,
} = useBackInStock()

const initPayload = () => ({
  title: isFinland.value ? '' : 'f',
  firstName: '',
  lastName: '',
  email: '',
})

// const resetState = ref(false)
const payload = reactive(initPayload())
const termsAccepted = ref()

onMounted(() => {
  termsAccepted.value = !doiRequired.value
})

const rules = computed(() => ({
  title: {},
  firstName: {
    required: $validation.rule.required,
    maxLength: $validation.rule.maxLength(50),
  },
  lastName: {
    required: $validation.rule.required,
    maxLength: $validation.rule.maxLength(50),
  },
  email: {
    required: $validation.rule.required,
    email: $validation.rule.email,
    maxLength: $validation.rule.maxLength(70),
  },
}))

const v = useVuelidate(rules.value, payload)
const submit = async () => {
  const isFormValid = await v.value.$validate()
  if (!isFormValid) {
    return
  }

  sendBackInStockRequest(payload)
}
</script>
